import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { SectionTitleComponent } from '../../../components/section-title/section-title.component';
import { REPOSITORY_PROVIDERS, RepositoryBinding, RepositoryPipeline } from '../../../models/repository-binding.model';
import { isGithubErrorCode } from '../../../models/github-project-config.model';

interface RepositoryRow {
  id: string;
  /** Die URL ohne Schema — das Design zeigt `github.com/owner/repo`. */
  shortUrl: string;
  provider: string;
  /** Der Schluessel des uebersetzten Namens; `null`, wenn es fuer den Provider keinen gibt. */
  providerKey: string | null;
  /** Leer, solange kein Branch gepflegt ist — die Zeile raet dann keinen. */
  branch: string | null;
}

/**
 * Karte „Repo-Anbindung" der Testlauf-Anlege-Seite (NEX-1106).
 *
 * <p>Zwei Auswahlschritte untereinander: erst das Repository, dann eine seiner
 * Pipelines. Die Schritte einer Pipeline stehen nicht hinter einem eigenen
 * Aufklapp-Schalter, sondern gehoeren zur Auswahl — so zeigt es das Design, und
 * so bleibt nur ein Bedienelement je Zeile.
 *
 * <p>Reine Darstellung: Laden und Zustand liegen bei der Seite, damit die Karte
 * nichts ueber Projekt-IDs oder Services wissen muss.
 */
@Component({
  selector: 'app-repo-anbindung',
  imports: [TranslatePipe, MatIcon, SectionTitleComponent],
  templateUrl: './repo-anbindung.component.html',
  styleUrl: './repo-anbindung.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepoAnbindungComponent {
  readonly repositories = input.required<RepositoryBinding[]>();
  readonly pipelines = input.required<RepositoryPipeline[]>();
  readonly selectedRepositoryId = input<string | null>(null);
  readonly selectedPipelineFile = input<string | null>(null);
  /** Der Branch, von dem die Pipelines gelesen wurden; `null`, solange keine vorliegen. */
  readonly readBranch = input<string | null>(null);
  readonly pipelinesLoading = input(false);
  /** Der Fehlercode des Backends, `null` solange nichts schiefgegangen ist. */
  readonly pipelinesErrorCode = input<string | null>(null);
  readonly repositoriesError = input(false);
  /** Im Bearbeiten-Modus ist die Zusammenstellung eines Laufs nicht mehr aenderbar (NEX-1100). */
  readonly disabled = input(false);

  readonly repositorySelected = output<string>();
  readonly pipelineSelected = output<string | null>();

  readonly rows = computed<RepositoryRow[]>(() =>
    this.repositories().map((repo) => ({
      id: repo.id,
      shortUrl: repo.repoUrl.replace(/^https?:\/\//, ''),
      provider: repo.provider,
      providerKey: REPOSITORY_PROVIDERS.some((known) => known === repo.provider)
        ? `testRunNew.repo.provider.${repo.provider}`
        : null,
      branch: repo.branch,
    })),
  );

  readonly hasSelectedRepository = computed(() => this.rows().some((row) => row.id === this.selectedRepositoryId()));

  readonly pipelinesFailed = computed(() => this.pipelinesErrorCode() !== null);

  readonly showPipelines = computed(
    () => this.hasSelectedRepository() && !this.pipelinesLoading() && !this.pipelinesFailed(),
  );

  /**
   * Nennt den Grund, den das Backend geliefert hat — fehlendes Token ist sonst eine Sackgasse.
   * Nur fuer bekannte Codes, sonst stuende bei einem neuen der rohe Uebersetzungsschluessel
   * auf der Seite.
   */
  readonly pipelineErrorKey = computed(() => {
    const code = this.pipelinesErrorCode();
    return code && isGithubErrorCode(code) ? `testRunNew.repo.errorCode.${code}` : 'testRunNew.repo.error';
  });

  /**
   * Die Trefferzeile nennt den Branch aus der Antwort. Fehlt er, nennt sie nur die
   * Zahl — ein geratener Name waere bei jedem Repository falsch, dessen Default
   * nicht `main` heisst.
   */
  readonly foundKey = computed(() => {
    const single = this.pipelines().length === 1;
    if (this.readBranch() === null) {
      return single ? 'testRunNew.repo.foundOneAnyBranch' : 'testRunNew.repo.foundOtherAnyBranch';
    }
    return single ? 'testRunNew.repo.foundOne' : 'testRunNew.repo.foundOther';
  });

  stepCountKey(count: number): string {
    return count === 1 ? 'testRunNew.repo.stepCountOne' : 'testRunNew.repo.stepCountOther';
  }

  onRepository(id: string): void {
    this.repositorySelected.emit(id);
  }

  /** Ein zweiter Klick auf dieselbe Pipeline hebt die Auswahl auf (Bundle: `on ? "" : p.id`). */
  onPipeline(file: string): void {
    this.pipelineSelected.emit(this.selectedPipelineFile() === file ? null : file);
  }
}
